import React, { useCallback, useEffect, useState } from 'react';
import { Tabs, Alert, Modal, message } from 'antd';
import { portalEventosService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import EventosDisponiveisList from './EventosDisponiveisList';
import MinhasInscricoesList from './MinhasInscricoesList';
import PagamentosEventosList from './PagamentosEventosList';

const { confirm } = Modal;

// Aba "Eventos" do Portal do Responsavel (Fase B.3b) -- auto-contida:
// busca seus proprios dados (nao depende do fetch inicial gigante do
// ResponsavelDashboard) e so recarrega quando o responsavel realmente
// abre a aba (mesmo espirito de nao disparar chamadas desnecessarias em
// telas que o usuario nunca visita).
const PortalEventosTab = () => {
  const [subTab, setSubTab] = useState('disponiveis');
  const [oportunidades, setOportunidades] = useState([]);
  const [minhasInscricoes, setMinhasInscricoes] = useState([]);
  const [pagamentos, setPagamentos] = useState([]);
  const [loadingOportunidades, setLoadingOportunidades] = useState(true);
  const [loadingMinhas, setLoadingMinhas] = useState(true);
  const [loadingPagamentos, setLoadingPagamentos] = useState(true);
  const [erroPagamentos, setErroPagamentos] = useState('');
  const [erro, setErro] = useState('');
  const [solicitandoKey, setSolicitandoKey] = useState(null);
  const [cancelandoKey, setCancelandoKey] = useState(null);

  const carregarOportunidades = useCallback(async () => {
    setLoadingOportunidades(true);
    try {
      const { data } = await portalEventosService.getOportunidades();
      setOportunidades(Array.isArray(data) ? data : []);
    } catch (err) {
      setErro(buildApiErrorMessage(err, 'Não foi possível carregar os eventos disponíveis.'));
    } finally {
      setLoadingOportunidades(false);
    }
  }, []);

  const carregarMinhasInscricoes = useCallback(async () => {
    setLoadingMinhas(true);
    try {
      const { data } = await portalEventosService.getMinhasInscricoes();
      setMinhasInscricoes(Array.isArray(data) ? data : []);
    } catch (err) {
      setErro(buildApiErrorMessage(err, 'Não foi possível carregar suas inscrições.'));
    } finally {
      setLoadingMinhas(false);
    }
  }, []);

  // Fase E.3 -- "Pagamentos" ("como e quando eu pago", read-only puro).
  // Estado de erro proprio (exibido dentro do proprio card, ver
  // PagamentosEventosList.js) -- nao reaproveita o `erro` compartilhado
  // acima, que hoje so' cobre as acoes de solicitar/cancelar inscricao.
  const carregarPagamentos = useCallback(async () => {
    setLoadingPagamentos(true);
    try {
      const { data } = await portalEventosService.getPagamentos();
      setPagamentos(Array.isArray(data) ? data : []);
      setErroPagamentos('');
    } catch (err) {
      setErroPagamentos(buildApiErrorMessage(err, 'Não foi possível carregar seus pagamentos.'));
    } finally {
      setLoadingPagamentos(false);
    }
  }, []);

  useEffect(() => {
    carregarOportunidades();
    carregarMinhasInscricoes();
    carregarPagamentos();
  }, [carregarOportunidades, carregarMinhasInscricoes, carregarPagamentos]);

  const handleSolicitar = useCallback(
    (oportunidade) => {
      const chave = `${oportunidade.id_evento_sessao}-${oportunidade.id_matricula}`;
      confirm({
        title: 'Solicitar inscrição',
        content: `Confirmar solicitação de inscrição de ${oportunidade.nome_aluno} em "${oportunidade.evento_nome} — ${oportunidade.sessao_nome}"?`,
        okText: 'Solicitar',
        cancelText: 'Cancelar',
        onOk: async () => {
          setSolicitandoKey(chave);
          try {
            await portalEventosService.solicitar(oportunidade.id_evento_sessao, oportunidade.id_matricula);
            message.success('Inscrição solicitada com sucesso.');
            await Promise.all([carregarOportunidades(), carregarMinhasInscricoes()]);
          } catch (err) {
            message.error(buildApiErrorMessage(err, 'Não foi possível solicitar a inscrição.'));
          } finally {
            setSolicitandoKey(null);
          }
        },
      });
    },
    [carregarOportunidades, carregarMinhasInscricoes]
  );

  const handleCancelar = useCallback(
    (oportunidade) => {
      confirm({
        title: 'Cancelar solicitação',
        content: `Confirmar cancelamento da solicitação de ${oportunidade.nome_aluno} em "${oportunidade.evento_nome} — ${oportunidade.sessao_nome}"?`,
        okText: 'Cancelar solicitação',
        okType: 'danger',
        cancelText: 'Voltar',
        onOk: async () => {
          setCancelandoKey(oportunidade.inscricao_id);
          try {
            await portalEventosService.cancelar(oportunidade.inscricao_id);
            message.success('Solicitação cancelada.');
            await Promise.all([carregarOportunidades(), carregarMinhasInscricoes()]);
          } catch (err) {
            message.error(buildApiErrorMessage(err, 'Não foi possível cancelar a solicitação.'));
          } finally {
            setCancelandoKey(null);
          }
        },
      });
    },
    [carregarOportunidades, carregarMinhasInscricoes]
  );

  return (
    <div>
      {erro && (
        <Alert type="error" message={erro} showIcon closable onClose={() => setErro('')} style={{ marginBottom: 16 }} />
      )}
      <Tabs
        activeKey={subTab}
        onChange={setSubTab}
        size="small"
        items={[
          {
            key: 'disponiveis',
            label: 'Eventos disponíveis',
            children: (
              <EventosDisponiveisList
                oportunidades={oportunidades}
                loading={loadingOportunidades}
                solicitandoKey={solicitandoKey}
                cancelandoKey={cancelandoKey}
                onSolicitar={handleSolicitar}
                onCancelar={handleCancelar}
              />
            ),
          },
          {
            key: 'minhas',
            label: 'Minhas inscrições',
            children: <MinhasInscricoesList inscricoes={minhasInscricoes} loading={loadingMinhas} />,
          },
          {
            key: 'pagamentos',
            label: 'Pagamentos',
            children: <PagamentosEventosList pagamentos={pagamentos} loading={loadingPagamentos} erro={erroPagamentos} />,
          },
        ]}
      />
    </div>
  );
};

export default PortalEventosTab;
